'use client'

import { useVirtualizer } from '@tanstack/react-virtual'
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import Filter from '../../../icons/Filter'
import { cn } from '../../../utils/cn'
import { matchesSearch } from '../../../utils/matchesSearch'
import { Text } from '../../typography/Text'
import { Button, type ButtonProps } from '../Button'
import { Checkbox } from '../Checkbox'
import { Command, CommandEmpty, CommandInput, CommandItem } from '../Command'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import { CategoryControls } from './CategoryControls'
import { MultiSelectClearButton } from './MultiSelectClearButton'

type VirtualizedMultiSelectOption = {
  value: string
  component: ReactNode
  keywords?: string[]
  category?: string | null
}

type VirtualizedMultiSelectProps = Pick<
  ComponentProps<typeof PopoverContent>,
  'align' | 'side' | 'sideOffset'
> & {
  label: string
  options: VirtualizedMultiSelectOption[]
  values: string[]
  onChange: (values: string[]) => void
  formatCategoryLabel?: (categoryId: string) => string
  triggerVariant?: ButtonProps['variant']
  triggerSize?: ButtonProps['size']
  triggerClassName?: string
  triggerIcon?: ReactNode
  showSelectedCount?: boolean
  estimateItemSize?: number
  searchPlaceholder?: string
  /**
   * Show an inline clear button when one or more values are selected. Set to
   * `false` when clearing all values would be unsafe.
   */
  showClearButton?: boolean
}

export function VirtualizedMultiSelect(props: VirtualizedMultiSelectProps) {
  const {
    label,
    options,
    values,
    onChange,
    formatCategoryLabel,
    align = 'start',
    side,
    sideOffset,
    triggerVariant = 'ghost',
    triggerSize = 'sm',
    triggerClassName,
    triggerIcon = <Filter className="size-5" />,
    showSelectedCount = true,
    estimateItemSize = 32,
    searchPlaceholder,
    showClearButton = true
  } = props

  const [open, setOpen] = useState(false)
  const [search, _setSearch] = useState('')
  const [activeValue, setActiveValue] = useState('')
  const [sortingCategory, setSortingCategory] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const selectedCount = values.length
  const triggerAriaLabel = `${label}${showSelectedCount && selectedCount > 0 ? ` (${selectedCount})` : ''}`

  function clearSelection() {
    onChange([])
    setSortingCategory(null)
  }

  const hasCategories = useMemo(() => {
    return options.some(opt => opt.category != null)
  }, [options])

  const sortedOptions = useMemo(() => {
    if (!sortingCategory) return options

    const categoryOptions: VirtualizedMultiSelectOption[] = []
    const otherOptions: VirtualizedMultiSelectOption[] = []

    for (const option of options) {
      if (option.category === sortingCategory) {
        categoryOptions.push(option)
      } else {
        otherOptions.push(option)
      }
    }

    return [...categoryOptions, ...otherOptions]
  }, [options, sortingCategory])

  const filteredOptions = useMemo(() => {
    if (!search) return sortedOptions
    return sortedOptions.filter(o => matchesSearch(o, search))
  }, [sortedOptions, search])

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateItemSize,
    overscan: 5
  })

  const parentRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node
      if (node) virtualizer.measure()
    },
    [virtualizer]
  )

  // Auto-select the first filtered option when no valid selection exists.
  // Without CommandList, cmdk can't auto-select via DOM queries, so we do it manually.
  useEffect(() => {
    if (filteredOptions.length === 0) return
    const hasMatch = filteredOptions.some(o => o.value.toLowerCase() === activeValue.toLowerCase())
    const first = filteredOptions[0]
    if (!hasMatch && first) {
      setActiveValue(first.value)
    }
  }, [filteredOptions, activeValue])

  // Scroll the virtualizer to keep the keyboard-active item visible
  useEffect(() => {
    if (!activeValue) return
    const index = filteredOptions.findIndex(
      o => o.value.toLowerCase() === activeValue.toLowerCase()
    )
    if (index !== -1) virtualizer.scrollToIndex(index)
  }, [activeValue, filteredOptions, virtualizer])

  function updateSearch(value: string) {
    _setSearch(value)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) updateSearch('')
  }

  function toggleValue(optionValue: string) {
    const newValues = values.includes(optionValue)
      ? values.filter(v => v !== optionValue)
      : [...values, optionValue]

    onChange(newValues)
  }

  function handleInputKeyDown(e: React.KeyboardEvent) {
    const count = filteredOptions.length
    if (count === 0) return

    const currentIndex = filteredOptions.findIndex(
      o => o.value.toLowerCase() === activeValue.toLowerCase()
    )

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      e.stopPropagation()
      const nextIndex = currentIndex < count - 1 ? currentIndex + 1 : 0
      const next = filteredOptions[nextIndex]
      if (next) setActiveValue(next.value)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : count - 1
      const prev = filteredOptions[prevIndex]
      if (prev) setActiveValue(prev.value)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      const current = filteredOptions[currentIndex]
      if (current) toggleValue(current.value)
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          aria-label={triggerAriaLabel}
          variant={triggerVariant}
          size={triggerSize}
          className={cn('text-secondary-foreground', triggerClassName)}
        >
          {triggerIcon}
          <Text size="2xs" className="gap-2xs flex items-center">
            {label}
            {showSelectedCount && selectedCount > 0 && (
              <span className="tabular-nums">({selectedCount})</span>
            )}
          </Text>
          {showClearButton && selectedCount > 0 && (
            <MultiSelectClearButton label={label} onClear={clearSelection} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align={align} side={side} sideOffset={sideOffset}>
        <Command shouldFilter={false} value={activeValue} onValueChange={setActiveValue}>
          <div className="flex h-9 items-center border-b *:data-[slot=command-input-wrapper]:flex-1">
            <CommandInput
              placeholder={searchPlaceholder ?? `Search for ${label.toLowerCase()}`}
              className="text-xs"
              value={search}
              onValueChange={updateSearch}
              onKeyDown={handleInputKeyDown}
            />
            {!hasCategories && (
              <Button
                disabled={selectedCount === 0}
                onClick={clearSelection}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    clearSelection()
                  }
                }}
                variant="ghost"
                size="sm"
                className="mr-2 ml-auto shrink-0"
              >
                Clear
              </Button>
            )}
          </div>
          {hasCategories && (
            <div className="flex min-h-9 w-full items-center border-b px-2">
              <CategoryControls
                options={options}
                values={values}
                formatCategoryLabel={formatCategoryLabel}
                onSelect={onChange}
                onSortingCategoryChange={setSortingCategory}
              />
            </div>
          )}
          {filteredOptions.length === 0 ? (
            <CommandEmpty>{`No ${label.toLowerCase()}s found.`}</CommandEmpty>
          ) : (
            <div
              ref={parentRef}
              className="max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto p-1"
            >
              <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
                {virtualizer.getVirtualItems().map(virtualRow => {
                  const option = filteredOptions[virtualRow.index]
                  if (!option) return null
                  return (
                    <CommandItem
                      key={option.value}
                      ref={virtualizer.measureElement}
                      data-index={virtualRow.index}
                      value={option.value}
                      onSelect={() => toggleValue(option.value)}
                      className="absolute top-0 left-0 w-full"
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <Checkbox checked={values.includes(option.value)} />
                      {option.component}
                    </CommandItem>
                  )
                })}
              </div>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export type { VirtualizedMultiSelectOption, VirtualizedMultiSelectProps }
